import { ReactElement, useCallback, useState } from 'react';
import { useToggle } from 'react-use';

import {
   DisplayableMessage,
   PatientIrremovabilityDisplayableMessageQuery,
   PatientSimple,
} from '@dreryk/egabinet-patient-frontend-api';

import { Translate } from '@dreryk/react-common';
import { Dropdown, MoreIconVertical, TooltipPlacement, useHistoryPushWithBackAction } from '@dreryk/react-components';
import { performCqrsQueryFind } from '@dreryk/command-query-react-common';

import { protectFeature } from '~libs.shared.system';
import { patientPaths } from '~libs.desktop.shared.paths';

import { useGoToPatientFile } from '../../common/common.hooks';
import { PatientRemoveModal } from '../../patient/remove/PatientRemoveModal';
import { patientTranslationKeys } from '../../translations';
import { PatientCannotBeRemovedModal } from '../../patient/remove/PatientCannotBeRemovedModal';

type RemovePatientDropdownItemProps = {
   readonly onClick: VoidFunction;
   readonly medicalDocumentationCreated: boolean;
   readonly onPatientIrremovability: (displayableMessage: DisplayableMessage) => void;
   readonly patientId: number;
};

const PatientRemoveDropdownItem = (props: RemovePatientDropdownItemProps): ReactElement => {
   const { onClick, patientId, onPatientIrremovability } = props;
   const handleClick = useCallback(
      async (hide: VoidFunction) => {
         const displayableMessage = await performCqrsQueryFind<DisplayableMessage>(() => {
            const query = new PatientIrremovabilityDisplayableMessageQuery();
            query.patientId = patientId;
            return query;
         });

         if (displayableMessage) {
            onPatientIrremovability(displayableMessage);
            return hide();
         }

         onClick();
         return hide();
      },
      [onClick, patientId, onPatientIrremovability]
   );
   return (
      <Dropdown.Item testId={'patientRemove'} onClick={handleClick} disabled={props.medicalDocumentationCreated}>
         <Dropdown.Item.Tooltip
            disabled={!props.medicalDocumentationCreated}
            testId={'patientRemoveBlocked'}
            placement={TooltipPlacement.LEFT}
            content={patientTranslationKeys.patientCannotBeRemovedReason}
         >
            <Translate>{patientTranslationKeys.removePatient}</Translate>
         </Dropdown.Item.Tooltip>
      </Dropdown.Item>
   );
};

type AdditionalOptionsCellProps = {
   readonly patient: PatientSimple;
   readonly onPatientRemoved: VoidFunction;
};

// eslint-disable-next-line max-lines-per-function
export const AdditionalOptionsCell = (props: AdditionalOptionsCellProps): JSX.Element => {
   const [patientRemoveModalVisible, togglePatientRemoveModalVisible] = useToggle(false);
   const [displayableMessage, setDisplayableMessage] = useState<DisplayableMessage | null>(null);
   const goToPatientFile = useGoToPatientFile(true);
   const push = useHistoryPushWithBackAction(true);
   const creatingAppointmentsAvailable = protectFeature('creatingAppointments');

   const patientId = props.patient.id;

   const handlePatientRemoved = useCallback<VoidFunction>(() => {
      props.onPatientRemoved();
      togglePatientRemoveModalVisible(false);
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [togglePatientRemoveModalVisible, props.onPatientRemoved]);

   return (
      <>
         <Dropdown
            testId={'additionalOptions'}
            triggerIcon={<MoreIconVertical />}
            triggerIconSuffixHidden
            disabled={'empty'}
         >
            <Dropdown.Item
               testId={'patientsFile'}
               onClick={(hide) => {
                  goToPatientFile(patientId, {
                     label: patientTranslationKeys.goBackToPatientList,
                  });
                  hide();
               }}
            >
               {patientTranslationKeys.patientsFile}
            </Dropdown.Item>
            {protectFeature(
               'searchingMedicalData',
               <Dropdown.Item
                  testId={'patientHistory'}
                  onClick={(hide) => {
                     push(`/historia-pacjenta/${patientId}`, {
                        label: patientTranslationKeys.goBackToPatientList,
                     });
                     hide();
                  }}
               >
                  {patientTranslationKeys.patientHistory}
               </Dropdown.Item>,
               null
            )}
            <Dropdown.Item
               testId={'goToCalendar'}
               onClick={(hide) => {
                  push(patientPaths.getCalendarWithBookingModePath(patientId), {
                     label: patientTranslationKeys.goBackToPatientList,
                  });
                  hide();
               }}
               disabled={!creatingAppointmentsAvailable}
            >
               <Dropdown.Item.Tooltip
                  testId={'goToCalendar'}
                  content={patientTranslationKeys.accountPartiallyBlocked}
                  disabled={creatingAppointmentsAvailable}
               >
                  <Translate>{patientTranslationKeys.patientRegisterVisit}</Translate>
               </Dropdown.Item.Tooltip>
            </Dropdown.Item>
            <PatientRemoveDropdownItem
               medicalDocumentationCreated={props.patient.medicalDocumentationCreated}
               onClick={togglePatientRemoveModalVisible}
               patientId={props.patient.id}
               onPatientIrremovability={setDisplayableMessage}
            />
         </Dropdown>
         {patientRemoveModalVisible ? (
            <PatientRemoveModal
               patient={props.patient}
               onCancel={togglePatientRemoveModalVisible}
               onPatientRemoved={handlePatientRemoved}
            />
         ) : null}
         {displayableMessage ? (
            <PatientCannotBeRemovedModal
               displayableMessage={displayableMessage}
               onClose={() => setDisplayableMessage(null)}
            />
         ) : null}
      </>
   );
};
